import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, Circle, Target, TrendingUp, Info, Sparkles, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { ProfileCompletionDetails as ProfileCompletionDetailsType } from '@/types/userProfile';
import { CompletionCategory } from '@/types/userProfile';
import { formatDistanceToNow } from 'date-fns';

interface ProfileCompletionDetailsProps {
  profileId: string;
  details: ProfileCompletionDetailsType;
  /**
   * Called when user wants to edit a specific missing field.
   * `fieldName` is passed when available so the caller can open the right editor (bio vs basic profile fields).
   */
  onEditMissingField?: (fieldName?: string) => void;
}

const CATEGORY_CONFIG: Record<
  CompletionCategory | 'critical' | 'highValue' | 'mediumValue' | 'lowValue',
  {
    label: string;
    color: string;
    bgColor: string;
    icon: React.ReactNode;
  }
> = {
  [CompletionCategory.CRITICAL]: {
    label: 'Critical',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    icon: <AlertCircle className="h-4 w-4" />,
  },
  critical: {
    label: 'Critical',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    icon: <AlertCircle className="h-4 w-4" />,
  },
  [CompletionCategory.HIGH_VALUE]: {
    label: 'High Value',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    icon: <TrendingUp className="h-4 w-4" />,
  },
  highValue: {
    label: 'High Value',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    icon: <TrendingUp className="h-4 w-4" />,
  },
  [CompletionCategory.MEDIUM_VALUE]: {
    label: 'Medium Value',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    icon: <Circle className="h-4 w-4" />,
  },
  mediumValue: {
    label: 'Medium Value',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    icon: <Circle className="h-4 w-4" />,
  },
  [CompletionCategory.LOW_VALUE]: {
    label: 'Low Value',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    icon: <Info className="h-4 w-4" />,
  },
  lowValue: {
    label: 'Low Value',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    icon: <Info className="h-4 w-4" />,
  },
};

const getCompletionColor = (percentage: number): string => {
  if (percentage >= 90) return 'text-emerald-600 dark:text-emerald-400';
  if (percentage >= 70) return 'text-green-600 dark:text-green-400';
  if (percentage >= 50) return 'text-yellow-600 dark:text-yellow-400';
  if (percentage >= 30) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
};

/** Coerce API value to string so we never render an object as a React child (avoids React #31). */
function toDisplayString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export const ProfileCompletionDetails: React.FC<ProfileCompletionDetailsProps> = ({
  details,
  onEditMissingField,
}) => {
  const { 
    completionPercentage = 0, 
    totalFields = 0, 
    completedFields = 0, 
    pendingFields = 0, 
    breakdown, 
    missingFields = [], 
    lastCalculatedAt 
  } = details;

  const defaultCategoryData = {
    weightPercentage: 0,
    totalFields: 0,
    completedFields: 0,
    pendingFields: 0,
    achievedWeight: 0,
  };

  const breakdownEntries = [
    { key: 'critical' as const, data: breakdown?.critical || defaultCategoryData },
    { key: 'highValue' as const, data: breakdown?.highValue || defaultCategoryData },
    { key: 'mediumValue' as const, data: breakdown?.mediumValue || defaultCategoryData },
    { key: 'lowValue' as const, data: breakdown?.lowValue || defaultCategoryData },
  ];

  const categoryProgress = (data: typeof defaultCategoryData) => {
    if (!data || data.totalFields === 0) return 100;
    return (data.completedFields / data.totalFields) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Overall Progress Hero Card */}
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-amber-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-amber-500/20 shadow-lg">
        <CardContent className="p-8">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-6">
            <div className="relative flex h-24 w-24 shrink-0 items-center justify-center">
              <svg className="h-24 w-24 -rotate-90 transform">
                <circle
                  cx="48"
                  cy="48"
                  r="44"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-muted/20"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="44"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 44}`}
                  strokeDashoffset={`${2 * Math.PI * 44 * (1 - completionPercentage / 100)}`}
                  className={getCompletionColor(completionPercentage)}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className={`text-2xl font-bold ${getCompletionColor(completionPercentage)}`}>
                    {Math.round(completionPercentage)}%
                  </div>
                  <div className="text-xs text-muted-foreground">Complete</div>
                </div>
              </div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl font-bold tracking-tight">Profile Completion</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {completedFields} of {totalFields} fields completed • {pendingFields} pending
              </p>
              <div className="mt-4">
                <Progress value={completionPercentage} className="h-2" />
              </div>
              {lastCalculatedAt && !isNaN(new Date(lastCalculatedAt).getTime()) && (
                <p className="text-xs text-muted-foreground mt-2">
                  Updated {formatDistanceToNow(new Date(lastCalculatedAt), { addSuffix: true })}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {breakdownEntries.map(({ key, data }) => {
          const config = CATEGORY_CONFIG[key];
          const progress = categoryProgress(data);
          const isComplete = data.pendingFields === 0;

          return (
            <Card key={key} className="relative overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <span className={config.color}>{config.icon}</span>
                    {config.label}
                  </CardTitle>
                  {isComplete && (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  )}
                </div>
                <CardDescription className="text-xs">
                  {data.completedFields}/{data.totalFields} fields • {data.achievedWeight.toFixed(1)}% weight
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-1.5" />
                </div>
                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-muted-foreground">Weight</span>
                  <Badge variant="outline" className={`text-xs ${config.bgColor} ${config.color} border-0`}>
                    {data.weightPercentage}%
                  </Badge>
                </div>
                {data.pendingFields > 0 && (
                  <div className="pt-1">
                    <span className="text-xs text-muted-foreground">
                      {data.pendingFields} field{data.pendingFields !== 1 ? 's' : ''} pending
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Missing Fields */}
      {missingFields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              Missing Fields
            </CardTitle>
            <CardDescription>
              Complete these fields to improve your profile completion score
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {missingFields.map((field, idx) => {
                const categoryKey = typeof field.category === 'string' ? field.category : 'lowValue';
                const config = CATEGORY_CONFIG[categoryKey as keyof typeof CATEGORY_CONFIG] || CATEGORY_CONFIG.lowValue;
                const displayName = toDisplayString(field.displayName);
                const description = toDisplayString(field.description);
                const example = toDisplayString(field.example);
                return (
                  <div
                    key={`${toDisplayString(field.fieldName)}-${idx}`}
                    className="rounded-lg border bg-muted/20 p-4 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{displayName}</h4>
                          <Badge
                            variant="outline"
                            className={`text-xs ${config.bgColor} ${config.color} border-0`}
                          >
                            {config.label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {(typeof field.weight === 'number' ? field.weight : 0).toFixed(1)}% weight
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{description}</p>
                        {example && (
                          <div className="flex items-start gap-1.5 text-xs text-muted-foreground mt-1">
                            <Sparkles className="h-3 w-3 mt-0.5 shrink-0" />
                            <span>
                              <span className="font-medium">Example:</span> {example}
                            </span>
                          </div>
                        )}
                      </div>
                      {onEditMissingField && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 gap-1.5"
                          onClick={() => onEditMissingField?.(field.fieldName)}
                          aria-label={`Edit ${displayName}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Fill in
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Complete State — only show when actually complete (not just empty data) */}
      {missingFields.length === 0 && completionPercentage >= 100 && (
        <Card className="border-dashed border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 dark:text-emerald-400 mb-4" />
            <p className="font-medium text-emerald-900 dark:text-emerald-100">Profile Complete!</p>
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
              All required fields have been completed. Great job!
            </p>
          </CardContent>
        </Card>
      )}

      {/* Incomplete but missing fields not calculated — prompt user to complete */}
      {missingFields.length === 0 && completionPercentage < 100 && (
        <Card className="border-dashed border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <Info className="h-10 w-10 text-amber-500 mb-3" />
            <p className="font-medium text-amber-900 dark:text-amber-100">
              Your profile is {completionPercentage}% complete
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Add more details to get personalized coaching and content.
            </p>
            {onEditMissingField && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4 gap-2"
                onClick={() => onEditMissingField()}
              >
                <Pencil className="h-4 w-4" />
                Complete Profile
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
